import { parseIncomeYen } from '../dependentDeduction/spouseSection/spouseEligibility';
import {
  DEPENDENT_CHANGE_REASON_CODES,
  isDependentChangeReasonCode,
  type DependentChangeReasonCode,
} from '../dependentDeduction/dependentsSection/dependentsSectionTypes';
import type { FamilyMemberDraft, YesNo } from './familyInfoTypes';

export {
  DEPENDENT_CHANGE_REASON_CODES,
  isDependentChangeReasonCode,
  type DependentChangeReasonCode,
};

/** Standard 源泉控除対象親族 income ceiling (合計所得金額 58万円以下). */
export const DEPENDENT_INCOME_LIMIT_YEN = 580_000;
/** 特定親族 (ages 19–22) upper income ceiling (合計所得金額 100万円以下). */
export const SPECIFIC_RELATIVE_INCOME_MAX_YEN = 1_000_000;

/** Stored as-is in tax_dependent_category.category (one row per person for family-info path). */
export const TAX_DEPENDENT_CATEGORY_KANJI = {
  under16: '16歳未満の扶養親族',
  general: '控除対象扶養親族',
  specific: '特定扶養親族',
  specific_relative: '特定親族',
  elderly: '老人扶養親族',
  cohabiting_elderly_parents: '同居老親等',
} as const;

export type TaxDependentCategoryKanji =
  (typeof TAX_DEPENDENT_CATEGORY_KANJI)[keyof typeof TAX_DEPENDENT_CATEGORY_KANJI];

export const ALL_TAX_DEPENDENT_CATEGORY_KANJI: TaxDependentCategoryKanji[] = [
  TAX_DEPENDENT_CATEGORY_KANJI.under16,
  TAX_DEPENDENT_CATEGORY_KANJI.specific,
  TAX_DEPENDENT_CATEGORY_KANJI.specific_relative,
  TAX_DEPENDENT_CATEGORY_KANJI.elderly,
  TAX_DEPENDENT_CATEGORY_KANJI.cohabiting_elderly_parents,
  TAX_DEPENDENT_CATEGORY_KANJI.general,
];

const TAX_DEPENDENT_CATEGORY_CODE_BY_KANJI: Record<TaxDependentCategoryKanji, string> = {
  [TAX_DEPENDENT_CATEGORY_KANJI.under16]: 'under16',
  [TAX_DEPENDENT_CATEGORY_KANJI.general]: 'general',
  [TAX_DEPENDENT_CATEGORY_KANJI.specific]: 'specific',
  [TAX_DEPENDENT_CATEGORY_KANJI.specific_relative]: 'specific_relative',
  [TAX_DEPENDENT_CATEGORY_KANJI.elderly]: 'elderly',
  [TAX_DEPENDENT_CATEGORY_KANJI.cohabiting_elderly_parents]: 'cohabiting_elderly_parents',
};

/** UI label for a stored category kanji, in the user's selected language. */
export function taxDependentCategoryLabel(kanji: string, t: (key: string) => string): string {
  const code = TAX_DEPENDENT_CATEGORY_CODE_BY_KANJI[kanji as TaxDependentCategoryKanji];
  if (!code) return kanji;
  return t(`submitDocuments.dependentDeduction.dependents.category.${code}`);
}

export type DependentIneligibilityReason =
  | 'no_shared_livelihood'
  | 'income_over_limit'
  | 'non_resident_no_support'
  | '';

export const DEPENDENT_NON_RESIDENT_REASON_CODES = [
  'study_abroad',
  'disability',
  'remittance_380k',
  'none',
] as const;

export type DependentNonResidentReasonCode = (typeof DEPENDENT_NON_RESIDENT_REASON_CODES)[number];

export function isDependentNonResidentReasonCode(
  value: string
): value is DependentNonResidentReasonCode {
  return (DEPENDENT_NON_RESIDENT_REASON_CODES as readonly string[]).includes(
    value as DependentNonResidentReasonCode
  );
}

/** Age as of Dec 31 of the tax year (local copy to avoid circular import with familyInfoSummary). */
export function ageAtTaxYearEnd(dateOfBirth: string, taxYear: number): number | null {
  const m = dateOfBirth.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const dob = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(dob.getTime())) return null;
  const end = new Date(taxYear, 11, 31);
  let age = end.getFullYear() - dob.getFullYear();
  const anniversary = new Date(taxYear, dob.getMonth(), dob.getDate());
  if (end < anniversary) age -= 1;
  return age;
}

/** Under-16 dependents cannot earn; skip estimated-income chat and store 0. */
export function isUnder16AtTaxYearEnd(dateOfBirth: string, taxYear: number): boolean {
  const age = ageAtTaxYearEnd(dateOfBirth, taxYear);
  return age != null && age < 16;
}

function isParentLike(relationship: string): boolean {
  return relationship === 'father' || relationship === 'mother';
}

export function isNonSpouseTaxFlow(d: FamilyMemberDraft): boolean {
  return d.relationship !== 'spouse' && d.claimTaxDependent === 'yes';
}

/** True when income exceeds the applicable ceiling for this dependent's age. */
export function incomeOverDependentLimit(
  estimatedIncome: string,
  dateOfBirth: string,
  taxYear: number
): boolean {
  const income = parseIncomeYen(estimatedIncome);
  const age = ageAtTaxYearEnd(dateOfBirth, taxYear);
  if (age != null && age >= 19 && age <= 22) {
    return income > SPECIFIC_RELATIVE_INCOME_MAX_YEN;
  }
  return income > DEPENDENT_INCOME_LIMIT_YEN;
}

/** Single primary category from DOB / income / household / relationship. */
export function suggestPrimaryTaxCategoryKanji(
  d: FamilyMemberDraft,
  taxYear: number
): TaxDependentCategoryKanji {
  const age = ageAtTaxYearEnd(d.dateOfBirth, taxYear);
  if (age != null) {
    if (age < 16) return TAX_DEPENDENT_CATEGORY_KANJI.under16;
    if (age >= 70 && d.livingInSameHousehold === 'yes' && isParentLike(d.relationship)) {
      return TAX_DEPENDENT_CATEGORY_KANJI.cohabiting_elderly_parents;
    }
    if (age >= 70) return TAX_DEPENDENT_CATEGORY_KANJI.elderly;
    if (age >= 19 && age <= 22) {
      const income = parseIncomeYen(d.estimatedIncome);
      if (income > DEPENDENT_INCOME_LIMIT_YEN && income <= SPECIFIC_RELATIVE_INCOME_MAX_YEN) {
        return TAX_DEPENDENT_CATEGORY_KANJI.specific_relative;
      }
      return TAX_DEPENDENT_CATEGORY_KANJI.specific;
    }
  }
  return TAX_DEPENDENT_CATEGORY_KANJI.general;
}

export function deriveDependentIneligibility(
  d: FamilyMemberDraft,
  taxYear: number
): DependentIneligibilityReason {
  if (!isNonSpouseTaxFlow(d)) return '';
  if (d.sharedLivelihood === 'no') return 'no_shared_livelihood';
  if (incomeOverDependentLimit(d.estimatedIncome, d.dateOfBirth, taxYear)) return 'income_over_limit';
  if (d.taxNonResident === 'yes' && d.nonResidentReason === 'none') {
    const age = ageAtTaxYearEnd(d.dateOfBirth, taxYear);
    if (age != null && age >= 30 && age < 70) return 'non_resident_no_support';
  }
  return '';
}

export function deriveDependentTaxEligible(d: FamilyMemberDraft, taxYear: number): YesNo {
  if (!isNonSpouseTaxFlow(d)) return '';
  const reason = deriveDependentIneligibility(d, taxYear);
  if (reason) return 'no';
  return 'yes';
}
